import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { livestreamService } from '../../services/livestream';
import { productService } from '../../services/products';
import { useForm, validationRules } from '../../hooks/useForm';
import Alert from '../../components/Common/Alert';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ImageUtils } from '../../utils/imageUtils';
import './Livestreams.css';

const LivestreamForm = () => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailError, setThumbnailError] = useState(null);
  const [thumbnailValidation, setThumbnailValidation] = useState(null);

  const {
    values,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    setFieldValue
  } = useForm(
    {
      title: '',
      description: '',
      category: '',
      subcategory: '',
      scheduled_time: '',
      is_scheduled: false
    },
    {
      title: [validationRules.required, validationRules.minLength(5)],
      description: [validationRules.required, validationRules.minLength(10)],
      category: [validationRules.required]
    }
  );


  // Load subcategories when category changes
  const loadSubcategories = useCallback(async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    try {
      setLoadingSubcategories(true);
      const subcategoriesData = await productService.getSubcategories(categoryId);
      setSubcategories(Array.isArray(subcategoriesData) ? subcategoriesData : (subcategoriesData?.results || []));
    } catch (error) {
      console.error('Error loading subcategories:', error);
      setSubcategories([]);
    } finally {
      setLoadingSubcategories(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [productsResponse, categoriesResponse] = await Promise.all([
        productService.getMyProducts(),
        productService.getCategories()
      ]);

      console.log('LivestreamForm: My products response:', productsResponse);
      console.log('LivestreamForm: Categories response:', categoriesResponse);

      // Ensure we always set an array for products
      const productsArray = Array.isArray(productsResponse) ? productsResponse : (productsResponse?.results || productsResponse?.data || []);
      console.log('LivestreamForm: Products array:', productsArray);
      setProducts(productsArray);

      // Set categories
      const categoriesArray = Array.isArray(categoriesResponse) ? categoriesResponse : (categoriesResponse?.results || categoriesResponse?.data || []);
      setCategories(categoriesArray);

    } catch (error) {
      console.error('Error loading data:', error);
      setProducts([]); // Set empty array on error
      setCategories([]);
      setAlert({
        type: 'danger',
        message: 'Failed to load data. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    handleChange('category', categoryId);
    handleChange('subcategory', ''); // Reset subcategory when category changes
    loadSubcategories(categoryId);
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    setThumbnailError(null);
    setThumbnailValidation(null);

    if (file) {
      try {
        // First validate the file type
        if (!file.type.startsWith('image/')) {
          setThumbnailError('Please select a valid image file');
          return;
        }

        // Check current dimensions
        const validation = await ImageUtils.validateImageDimensions(file);

        let finalFile = file;

        if (!validation.isValid) {
          // Auto-resize the image to required dimensions
          setAlert({
            type: 'info',
            message: `Resizing image from ${validation.width}×${validation.height}px to 480×720px...`,
            autoClose: 3000
          });

          try {
            finalFile = await ImageUtils.resizeLivestreamThumbnail(file);
            setAlert({
              type: 'success',
              message: '✅ Image automatically resized to 480×720px!',
              autoClose: 3000
            });
          } catch (resizeError) {
            console.error('Auto-resize failed:', resizeError);
            setThumbnailError('Failed to auto-resize image. Please manually resize to 480×720px and try again.');
            setThumbnailFile(null);
            setThumbnailPreview(null);
            return;
          }
        }

        setThumbnailFile(finalFile);
        setThumbnailValidation({
          isValid: true,
          width: 480,
          height: 720,
          message: 'Perfect! Image dimensions: 480×720px'
        });

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          setThumbnailPreview(e.target.result);
        };
        reader.readAsDataURL(finalFile);

      } catch (error) {
        console.error('Error processing thumbnail:', error);
        setThumbnailError('Failed to process image. Please try again.');
        setThumbnailFile(null);
        setThumbnailPreview(null);
      }
    }
  };

  const removeThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setThumbnailError(null);
    setThumbnailValidation(null);
    // Reset the file input
    const fileInput = document.getElementById('thumbnail');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const onSubmit = async (formData) => {
    try {
      if (!thumbnailFile) {
        setAlert({
          type: 'warning',
          message: 'Please upload a thumbnail image for the livestream. Any image will be automatically resized to fit!'
        });
        return;
      }

      if (selectedProducts.length === 0) {
        setAlert({
          type: 'warning',
          message: 'Please select at least one product for the livestream.'
        });
        return;
      }

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      if (formData.subcategory) {
        formDataToSend.append('subcategory', formData.subcategory);
      }

      // Add each product ID separately
      selectedProducts.forEach(productId => {
        formDataToSend.append('products', productId);
      });

      if (formData.is_scheduled && formData.scheduled_time) {
        formDataToSend.append('scheduled_start_time', formData.scheduled_time);
      }

      if (thumbnailFile) {
        formDataToSend.append('thumbnail', thumbnailFile);
      }

      console.log('LivestreamForm: Sending data with thumbnail:', {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        products: selectedProducts,
        scheduled_start_time: formData.is_scheduled ? formData.scheduled_time : null,
        thumbnail: thumbnailFile ? thumbnailFile.name : null
      });

      const response = await livestreamService.createLivestream(formDataToSend);

      setAlert({
        type: 'success',
        message: 'Livestream created successfully!'
      });

      setTimeout(() => {
        // If no scheduled time was set, start livestream immediately and go to control panel
        if (!formData.is_scheduled || !formData.scheduled_time) {
          navigate(`/livestreams/${response.id}/control?autoStart=true`);
        } else if (response.status === 'live') {
          navigate(`/livestreams/${response.id}/control`);
        } else {
          navigate('/livestreams');
        }
      }, 1500);
    } catch (error) {
      console.error('Error creating livestream:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Failed to create livestream. Please try again.';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else {
          // Handle field-specific errors
          const fieldErrors = Object.entries(error.response.data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      }

      setAlert({
        type: 'danger',
        message: errorMessage
      });
    }
  };

  if (loading) {
    return <LoadingSpinner text="Loading products..." />;
  }

  return (
    <div className="min-h-screen bg-dark-950 p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/livestreams')}
              className="w-10 h-10 rounded-xl bg-dark-800/50 border border-transparent text-gray-400 hover:text-white flex items-center justify-center transition-all hover:scale-110"
            >
              <i className="bi bi-arrow-left text-lg"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Broadcast Setup</h1>
              <p className="text-gray-500 text-xs font-bold uppercase tracking-[0.2em]">Deployment Console</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-800/40 backdrop-blur-xl rounded-2xl border border-transparent shadow-2xl overflow-hidden">
          <div className="p-6 md:p-10">
            {alert && (
              <Alert
                type={alert.type}
                message={alert.message}
                onClose={() => setAlert(null)}
                className="mb-8 border-transparent text-xs font-bold"
              />
            )}

            <form onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit);
            }}>
              {/* Basic Information */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                   <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center border border-red-500/20">
                      <i className="bi bi-info-circle-fill text-red-500 text-sm"></i>
                   </div>
                   <h5 className="text-sm font-bold text-white uppercase tracking-widest">General Configuration</h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label htmlFor="title" className="block text-gray-500 text-xs font-bold uppercase tracking-widest mb-2">
                      Broadcast Identifier <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full bg-dark-900/50 border ${errors.title ? 'border-red-500' : 'border-transparent'} rounded-xl px-4 py-3 text-white text-xs font-medium focus:outline-none focus:border-red-500/50 transition-all placeholder:text-gray-700`}
                      id="title"
                      value={values.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder="e.target.title"
                    />
                    {errors.title && (
                      <div className="text-red-500 text-xs font-bold uppercase tracking-widest mt-2">{errors.title}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={`w-full bg-dark-900 border ${errors.category ? 'border-red-500' : 'border-dark-700'} rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500 transition-colors appearance-none`}
                      id="category"
                      value={values.category}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                      <option value="" className="text-gray-500">Select Category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {errors.category && (
                      <div className="text-red-500 text-sm mt-1">{errors.category}</div>
                    )}
                  </div>

                  {/* Subcategory Selection */}
                  {values.category && (
                    <div>
                      <label htmlFor="subcategory" className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">
                        Subcategory
                        {loadingSubcategories && (
                          <span className="ml-2 inline-block w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></span>
                        )}
                      </label>
                      <select
                        className="w-full bg-dark-900 border border-dark-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500 transition-colors appearance-none"
                        id="subcategory"
                        value={values.subcategory}
                        onChange={(e) => handleChange('subcategory', e.target.value)}
                        disabled={loadingSubcategories || subcategories.length === 0}
                      >
                        <option value="" className="text-gray-500">
                          {subcategories.length === 0
                            ? 'No subcategories available'
                            : 'Select Subcategory (Optional)'}
                        </option>
                        {subcategories.map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </option>
                        ))}
                      </select>
                      <small className="block mt-1 text-gray-500 text-xs">
                        Optionally select a subcategory
                      </small>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <label htmlFor="description" className="block text-gray-500 text-xs font-bold uppercase tracking-widest mb-2">
                      Event Summary <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className={`w-full bg-dark-900/50 border ${errors.description ? 'border-red-500' : 'border-transparent'} rounded-xl px-4 py-3 text-white text-xs font-medium focus:outline-none focus:border-red-500/50 transition-all h-24 resize-none placeholder:text-gray-700`}
                      id="description"
                      rows="3"
                      value={values.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Details of the broadcast event..."
                    ></textarea>
                    {errors.description && (
                      <div className="text-red-500 text-xs font-bold uppercase tracking-widest mt-2">{errors.description}</div>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="thumbnail" className="block text-gray-500 text-xs font-bold uppercase tracking-widest mb-3">
                      Visual Identity <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="md:col-span-2">
                        <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-dark-700/50 transition-colors ${thumbnailError ? 'border-red-500 bg-red-500/10' : 'border-dark-700 bg-dark-900'}`}>
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <i className="bi bi-cloud-upload text-3xl text-gray-500 mb-2"></i>
                              <p className="mb-1 text-sm text-gray-400"><span className="font-bold">Click to upload</span> or drag and drop</p>
                              <p className="text-xs text-gray-500">PNG, JPG or GIF (Max 5MB)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            id="thumbnail"
                            accept="image/*"
                            onChange={handleThumbnailChange}
                            name="thumbnail_upload"
                          />
                        </label>
                        
                        <div className="mt-2 text-sm">
                          <div className="text-blue-400 text-xs font-bold flex items-center mb-1">
                            <i className="bi bi-info-circle mr-1"></i>
                            Auto-resized to 480×720px
                          </div>
                          <div className="text-gray-500 text-xs">
                            Images will be automatically resized to 480×720px
                          </div>
                        </div>
                        {thumbnailError && (
                          <div className="text-red-500 text-sm mt-1">
                            {thumbnailError}
                          </div>
                        )}
                        {thumbnailValidation && thumbnailValidation.isValid && (
                          <div className="text-green-500 text-sm mt-1 flex items-center">
                            <i className="bi bi-check-circle mr-1"></i>
                            {thumbnailValidation.message}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-center md:justify-start">
                        {thumbnailPreview ? (
                          <div className="relative group">
                            <img
                              src={thumbnailPreview}
                              alt="Thumbnail preview"
                              className="rounded-lg object-cover border border-dark-700 w-32 h-48 md:w-40 md:h-60"
                            />
                            <button
                              type="button"
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                              onClick={removeThumbnail}
                              title="Remove thumbnail"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        ) : (
                          <div
                            className="w-32 h-48 md:w-40 md:h-60 rounded-lg border-2 border-dashed border-dark-700 bg-dark-900 flex flex-col items-center justify-center text-gray-500"
                          >
                            <i className="bi bi-image text-3xl mb-2 opacity-50"></i>
                            <div className="text-xs text-center px-2">Preview</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

                {/* Scheduling */}
                <div className="mb-8">
                  <div className="mb-6 pb-2 border-b border-dark-700">
                    <h5 className="text-lg font-bold text-white flex items-center">
                      <i className="bi bi-calendar-event mr-2 text-primary-500"></i>
                      Schedule
                    </h5>
                  </div>
                  
                  <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
                    <div className="mb-4">
                      <label className="flex items-center cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={values.is_scheduled}
                            onChange={(e) => setFieldValue('is_scheduled', e.target.checked)}
                            className="sr-only"
                          />
                          <div className={`block w-14 h-8 rounded-full transition-colors ${values.is_scheduled ? 'bg-primary-500' : 'bg-dark-700'}`}></div>
                          <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${values.is_scheduled ? 'transform translate-x-6' : ''}`}></div>
                        </div>
                        <div className="ml-3 text-white font-medium">
                          Schedule for later <span className="text-gray-500 text-sm font-normal ml-1">(otherwise go live immediately)</span>
                        </div>
                      </label>
                    </div>

                    {values.is_scheduled && (
                      <div className="pl-2 border-l-2 border-dark-700 ml-2">
                        <label htmlFor="scheduled_time" className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-2 pl-4">
                          Scheduled Time <span className="text-red-500">*</span>
                        </label>
                        <div className="pl-4 max-w-md">
                          <input
                            type="datetime-local"
                            className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500 transition-colors"
                            id="scheduled_time"
                            value={values.scheduled_time}
                            onChange={(e) => handleChange('scheduled_time', e.target.value)}
                            min={new Date().toISOString().slice(0, 16)}
                          />
                          <small className="block mt-1 text-gray-500 text-xs">
                            Select when you want to start the livestream
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Selection */}
                <div className="mb-8">
                  <div className="mb-6 pb-2 border-b border-dark-700">
                    <h5 className="text-lg font-bold text-white flex items-center">
                      <i className="bi bi-box-seam mr-2 text-primary-500"></i>
                      Select Products for Bidding
                    </h5>
                  </div>

                  {(!products || products.length === 0) ? (
                    <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 p-4 rounded-lg flex items-center">
                      <i className="bi bi-exclamation-triangle mr-2 text-xl"></i>
                      <span>
                        You don't have any products yet.
                        <a href="/products/create" className="underline font-bold ml-1 hover:text-yellow-400">Create your first product</a>
                      </span>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-400 text-sm mb-4">
                        Select the products you want to feature in this livestream.
                        You can start bidding for these products during the live session.
                      </p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {(products || []).map((product) => (
                          <div 
                            key={product.id} 
                            className={`group relative bg-dark-900/50 rounded-2xl overflow-hidden border transition-all cursor-pointer ${selectedProducts.includes(product.id) ? 'border-red-500 ring-4 ring-red-500/10 scale-[0.98]' : 'border-transparent hover:border-red-500/30'}`}
                            onClick={() => handleProductSelection(product.id)}
                          >
                            <div className="relative aspect-square">
                              <img
                                src={ImageUtils.getProductImageUrl(product)}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                alt={product.name}
                                onError={(e) => { e.target.src = ImageUtils.getPlaceholderImage(); }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-transparent to-transparent opacity-80"></div>
                              
                              {selectedProducts.includes(product.id) && (
                                <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center backdrop-blur-[2px]">
                                   <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg transform scale-125 animate-pop">
                                      <i className="bi bi-check-lg text-lg"></i>
                                   </div>
                                </div>
                              )}
                            </div>
                            
                            <div className="p-3">
                              <h6 className="text-xs font-bold text-white mb-1 line-clamp-1 truncate uppercase tracking-tight">{product.name}</h6>
                              <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-red-500 tracking-tighter">
                                  ₹{parseFloat(product.base_price || product.price || 0).toLocaleString()}
                                </span>
                                <span className="text-xs font-bold text-gray-600 uppercase">QTY: {product.available_quantity ?? product.quantity ?? 0}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {selectedProducts.length > 0 && (
                        <div className="mt-6 bg-primary-500/10 border border-primary-500/20 text-primary-500 p-4 rounded-lg flex items-center">
                          <i className="bi bi-info-circle mr-2 text-xl"></i>
                          <span className="font-medium">
                            {selectedProducts.length} product(s) selected for this livestream
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Submit Buttons */}
                <div className="pt-8 border-t border-transparent flex justify-end gap-3">
                  <button
                    type="button"
                    className="px-6 py-2.5 rounded-xl border border-transparent text-gray-500 font-bold text-xs uppercase tracking-widest hover:bg-white/5 hover:text-white transition-all underline-offset-4"
                    onClick={() => navigate('/livestreams')}
                  >
                    Abort
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-2.5 rounded-xl bg-red-500 text-white font-bold text-xs uppercase tracking-widest hover:bg-red-600 transition-all shadow-lg shadow-red-500/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                    disabled={isSubmitting || products.length === 0 || !thumbnailFile || thumbnailError}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-3 h-3 border-2 border-transparent border-t-white rounded-full animate-spin"></div>
                        Transmitting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-broadcast"></i>
                        {values.is_scheduled ? 'Authorize Schedule' : 'Execute Broadcast'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
        </div>
      </div>
    </div>
  );
};

export default LivestreamForm;




